import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useSubscription } from '../../context/SubscriptionContext';

const UninvoicedShipments = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const { userData } = useAuth();
  const { hasRestriction } = useSubscription();
  const navigate = useNavigate();

  // Styles similar to the existing application
  const styles = {
    container: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: 'transparent',
      padding: '24px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    titleSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#0f172a',
      margin: 0,
      letterSpacing: '-0.5px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#64748b',
      margin: 0
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    actionBar: {
      background: 'linear-gradient(135deg, #06C167 0%, #764ba2 100%)',
      padding: '16px 24px',
      borderRadius: '12px',
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
    },
    selectedInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '600'
    },
    button: {
      padding: '12px 24px',
      border: 'none',
      background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
      color: 'white',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    successButton: {
      padding: '12px 24px',
      border: 'none',
      background: '#10b981',
      color: 'white',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    exportButton: {
      padding: '12px 24px',
      border: '1px solid #e2e8f0',
      background: 'white',
      color: '#06C167',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    box: {
      background: 'white',
      padding: '32px',
      borderRadius: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '32px',
      border: '1px solid #e2e8f0'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      padding: '24px',
      borderRadius: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '500'
    },
    statValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#0f172a'
    },
    filtersRow: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    filterSelect: {
      padding: '12px 16px',
      fontSize: '14px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      background: 'white',
      outline: 'none',
      cursor: 'pointer',
      minWidth: '200px'
    },
    filterGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filterIcon: {
      color: '#64748b'
    },
    exportSection: {
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '2px solid #f1f5f9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '20px'
    },
    exportHeader: {
      flex: 1
    },
    exportTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#0f172a',
      margin: '0 0 8px 0'
    },
    exportDescription: {
      fontSize: '14px',
      color: '#64748b',
      margin: 0
    },
    exportButtonPrimary: {
      padding: '16px 32px',
      border: 'none',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    exportButtonDisabled: {
      padding: '16px 32px',
      border: 'none',
      background: '#e2e8f0',
      color: '#94a3b8',
      borderRadius: '12px',
      cursor: 'not-allowed',
      fontSize: '14px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' },
    th: { background: '#f8fafc', padding: '16px 20px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#374151' },
    formGroup: { marginBottom: '16px' },
    input: { padding: '12px 16px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', width: '100%', background: '#fff', outline: 'none' },
    searchInputContainer: {
      position: 'relative',
      flex: 1,
      minWidth: '300px'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#94a3b8',
      width: '16px',
      height: '16px'
    },
    actionBtn: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '600',
      fontSize: '12px',
      cursor: 'pointer',
      background: '#f8fafc',
      color: '#06C167',
      transition: 'all 0.2s',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    actionBtnSuccess: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '600',
      fontSize: '12px',
      cursor: 'pointer',
      background: '#dcfce7',
      color: '#166534',
      transition: 'all 0.2s',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'inline-block'
    }
  };

  useEffect(() => {
    if (!userData?.companyId) {
      setLoading(false);
      return;
    }

    const companyId = userData.companyId;

    // Fetch orders that have been shipped but not invoiced
    const ordersQuery = query(
      collection(db, 'companies', companyId, 'orders'),
      where('status', 'in', ['Sevk Edildi', 'Kısmen Gönderildi', 'Tamamlandı']), // Shipped, partially shipped, or completed orders
      orderBy('orderDate', 'desc')
    );

    const unsubscribe = onSnapshot(ordersQuery, async (snapshot) => {
      try {
        const shipmentPromises = snapshot.docs.map(async (orderDoc) => {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          try {
            const shipmentsSnapshot = await getDocs(query(
              collection(db, 'companies', companyId, 'orders', orderDoc.id, 'shipments')
            ));

            return shipmentsSnapshot.docs
              .map(shipmentDoc => ({
                id: shipmentDoc.id,
                ...shipmentDoc.data(),
                orderId: orderDoc.id,
                orderNumber: orderData.orderNumber || orderDoc.id.substring(0, 8),
                customer: orderData.customerName,
                customerId: orderData.customerId,
                orderDate: orderData.orderDate,
                totalAmount: orderData.total
              }))
              .filter(shipment => shipment.status !== 'Faturalandı');
          } catch (error) {
            console.error('Error fetching shipments for order:', orderDoc.id, error);
            return [];
          }
        });

        const results = await Promise.all(shipmentPromises);
        const uninvoicedShipments = results.flat();

        setShipments(uninvoicedShipments);
        setLoading(false);
      } catch (error) {
        console.error("Snapshot error:", error);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [userData]);

  // Filter shipments based on search term and status
  const filteredShipments = useMemo(() => {
    let result = shipments;

    // Apply search filter
    if (searchTerm) {
      result = result.filter(shipment =>
        shipment.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipment.orderNumber?.toString().includes(searchTerm) ||
        shipment.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (shipment.items || []).some(item => item.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(shipment => shipment.status === filterStatus);
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = a.shipmentDate?.seconds || 0;
        const dateB = b.shipmentDate?.seconds || 0;
        return dateB - dateA; // Newest first
      } else if (sortBy === 'customer') {
        return (a.customer || '').localeCompare(b.customer || '');
      } else if (sortBy === 'orderNumber') {
        return (a.orderNumber || '').localeCompare(b.orderNumber || '');
      }
      return 0;
    });

    return result;
  }, [shipments, searchTerm, filterStatus, sortBy]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalShipments = shipments.length;
    const totalCustomers = new Set(shipments.map(s => s.customerId)).size;
    const totalItems = shipments.reduce((sum, s) => sum + (s.items || []).length, 0);
    
    return {
      totalShipments,
      totalCustomers,
      totalItems
    };
  }, [shipments]);

  // Handle Excel export
  const handleExportToExcel = () => {
    // Check if Excel export is allowed for current subscription
    if (hasRestriction('excelExport')) {
      alert('Excel dışa aktarma özelliği sadece ücretli planlarda mevcuttur. Lütfen aboneliğinizi yükseltin.');
      return;
    }

    try {
      // Prepare data for export
      const exportData = filteredShipments.map(shipment => ({
        'Sipariş No': shipment.orderNumber || shipment.orderId.substring(0, 8),
        'Müşteri': shipment.customer || '-',
        'Sevkiyat Tarihi': shipment.shipmentDate
          ? new Date(shipment.shipmentDate.seconds * 1000).toLocaleDateString('tr-TR')
          : '-',
        'Durum': shipment.status || '-',
        'Ürünler': (shipment.items || []).map(item => `${item.name} (${item.quantity})`).join(', '),
        'Toplam Ürün Sayısı': (shipment.items || []).length,
        'Sipariş ID': shipment.orderId
      }));

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 15 }, // Sipariş No
        { wch: 25 }, // Müşteri
        { wch: 15 }, // Sevkiyat Tarihi
        { wch: 15 }, // Durum
        { wch: 50 }, // Ürünler
        { wch: 15 }, // Toplam Ürün Sayısı
        { wch: 20 }  // Sipariş ID
      ];
      worksheet['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Faturalandırılmamış Sevkiyatlar");

      // Save file
      const fileName = `Faturalandirilmamis_Sevkiyatlar_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel dosyası oluşturulurken bir hata oluştu.');
    }
  };

  const handleSelectShipment = (shipmentId) => {
    setSelectedShipments(prev => {
      if (prev.includes(shipmentId)) {
        return prev.filter(id => id !== shipmentId);
      } else {
        return [...prev, shipmentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedShipments.length === filteredShipments.length) {
      setSelectedShipments([]);
    } else {
      setSelectedShipments(filteredShipments.map(shipment => shipment.id));
    }
  };

  const handleCreateInvoice = (shipmentId) => {
    const shipment = shipments.find(s => s.id === shipmentId);
    if (shipment) {
      // Navigate to invoice creation page with shipment data
      navigate('/faturalar/yeni', { state: { shipmentData: shipment, orderId: shipment.orderId } });
    }
  };

  const handleBulkInvoice = () => {
    if (selectedShipments.length > 0) {
      // For bulk invoicing, we'll navigate to invoice creation with multiple shipments
      const selectedShipmentObjects = shipments.filter(shipment => selectedShipments.includes(shipment.id));
      // Group by order ID for proper invoicing
      const shipmentsByOrder = selectedShipmentObjects.reduce((acc, shipment) => {
        if (!acc[shipment.orderId]) {
          acc[shipment.orderId] = [];
        }
        acc[shipment.orderId].push(shipment);
        return acc;
      }, {});

      navigate('/faturalar/yeni', { state: { shipmentsByOrder } });
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Faturalaştırılmamış Sevkiyatlar</h2>
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <div>Yükleniyor...</div>
        </div>
      </div>
    );
  }

  // Helper function to get status badge style
  const getStatusBadge = (status) => {
    const statusColors = {
      'Sevk Edildi': { background: '#dbeafe', color: '#1e40af' },
      'Kısmen Gönderildi': { background: '#fef3c7', color: '#92400e' },
      'Tamamlandı': { background: '#dcfce7', color: '#166534' }
    };
    const colors = statusColors[status] || { background: '#f1f5f9', color: '#475569' };
    
    return (
      <span style={{ ...styles.statusBadge, ...colors }}>
        {status || 'Bilinmiyor'}
      </span>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <h2 style={styles.title}>📦 Faturalandırılmamış Sevkiyatlar</h2>
          <p style={styles.subtitle}>
            Faturalandırılmayı bekleyen sevkiyatları görüntüleyin, yönetin ve Excel'e aktarın
          </p>
        </div>
      </div>

      {/* Summary Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Toplam Sevkiyat</span>
          <span style={styles.statValue}>{summaryStats.totalShipments}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Müşteri Sayısı</span>
          <span style={styles.statValue}>{summaryStats.totalCustomers}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Toplam Ürün</span>
          <span style={styles.statValue}>{summaryStats.totalItems}</span>
        </div>
      </div>

      {/* Action Bar with Export and Bulk Actions */}
      {selectedShipments.length > 0 && (
        <div style={styles.actionBar}>
          <div style={styles.selectedInfo}>
            <span style={{ fontSize: '20px' }}>📦</span>
            <span>{selectedShipments.length} sevkiyat seçildi</span>
          </div>
          <button style={styles.successButton} onClick={handleBulkInvoice}>
            <span style={{ fontSize: '18px' }}>📄</span>
            <span>Toplu Fatura Oluştur</span>
          </button>
        </div>
      )}

      {/* Filters and Search */}
      <div style={styles.box}>
        <div style={styles.filtersRow}>
          <div style={styles.searchInputContainer}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Müşteri adı, sipariş numarası, ID veya ürün adı ile ara..."
              style={{ ...styles.input, paddingLeft: '44px', width: '100%' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={styles.filterGroup}>
            <span style={styles.filterIcon}>🎯</span>
            <select 
              style={styles.filterSelect}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Tüm Durumlar</option>
              <option value="Sevk Edildi">Sevk Edildi</option>
              <option value="Kısmen Gönderildi">Kısmen Gönderildi</option>
              <option value="Tamamlandı">Tamamlandı</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <span style={styles.filterIcon}>📊</span>
            <select 
              style={styles.filterSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Tarihe Göre (Yeni → Eski)</option>
              <option value="customer">Müşteri Adına Göre (A → Z)</option>
              <option value="orderNumber">Sipariş Numarasına Göre</option>
            </select>
          </div>
        </div>

        {/* Export Section */}
        <div style={styles.exportSection}>
          <div style={styles.exportHeader}>
            <h3 style={styles.exportTitle}>📊 Verileri Dışa Aktar</h3>
            <p style={styles.exportDescription}>
              Filtrelenmiş {filteredShipments.length} sevkiyatı Excel dosyası olarak indirebilirsiniz
            </p>
          </div>
          <button 
            style={filteredShipments.length === 0 ? styles.exportButtonDisabled : styles.exportButtonPrimary} 
            onClick={handleExportToExcel}
            disabled={filteredShipments.length === 0}
            onMouseEnter={(e) => {
              if (filteredShipments.length > 0) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
          >
            <span style={{ fontSize: '24px' }}>📥</span>
            <div>
              <div style={{ fontWeight: '600' }}>Excel Olarak İndir</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Tüm filtrelenmiş veriler</div>
            </div>
          </button>
        </div>
      </div>

      {/* Shipments Table */}
      <div style={styles.box}>
        {filteredShipments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📦</div>
            <h4 style={{ color: '#64748b', marginBottom: '16px' }}>Faturalaştırılmamış sevkiyat bulunamadı</h4>
            <p style={{ color: '#94a3b8' }}>
              {shipments.length === 0 
                ? 'Tüm sevkiyatlar faturalandırılmış veya henüz sevk edilmemiş siparişler mevcut.'
                : 'Filtrelere uygun sevkiyat bulunamadı. Filtreleri değiştirmeyi deneyin.'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedShipments.length === filteredShipments.length && filteredShipments.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={styles.th}>Sipariş Numarası</th>
                  <th style={styles.th}>Müşteri</th>
                  <th style={styles.th}>Durum</th>
                  <th style={styles.th}>Sevkiyat Tarihi</th>
                  <th style={styles.th}>Ürünler</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredShipments.map((shipment) => (
                  <tr 
                    key={shipment.id} 
                    style={{ 
                      background: selectedShipments.includes(shipment.id) ? '#f1f5f9' : 'transparent',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedShipments.includes(shipment.id)) {
                        e.currentTarget.style.background = '#f8fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedShipments.includes(shipment.id)) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedShipments.includes(shipment.id)}
                        onChange={() => handleSelectShipment(shipment.id)}
                      />
                    </td>
                    <td style={styles.td}>
                      <strong style={{ color: '#06C167' }}>
                        #{shipment.orderNumber || shipment.orderId.substring(0, 8)}
                      </strong>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '500' }}>{shipment.customer}</div>
                    </td>
                    <td style={styles.td}>
                      {getStatusBadge(shipment.status)}
                    </td>
                    <td style={styles.td}>
                      {shipment.shipmentDate
                        ? new Date(shipment.shipmentDate.seconds * 1000).toLocaleDateString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })
                        : '-'}
                    </td>
                    <td style={styles.td}>
                      <div style={{ maxWidth: '400px' }}>
                        {(shipment.items || []).slice(0, 2).map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '4px', fontSize: '13px' }}>
                            <strong>{item.name}</strong> × {item.quantity}
                          </div>
                        ))}
                        {(shipment.items || []).length > 2 && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            +{(shipment.items || []).length - 2} ürün daha
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <button
                        style={styles.actionBtnSuccess}
                        onClick={() => handleCreateInvoice(shipment.id)}
                        onMouseEnter={(e) => e.target.style.background = '#bbf7d0'}
                        onMouseLeave={(e) => e.target.style.background = '#dcfce7'}
                      >
                        🧾 Fatura Oluştur
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UninvoicedShipments;